import React, { useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import moment from "moment"
import {
	Box,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	ListSubheader,
	TextField,
	Tooltip,
	Checkbox,
	ListItemText,
	Typography,
	Paper,
	InputAdornment,
	Grid,
} from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { KeyboardArrowDown, Clear as ClearIcon, Search as SearchIcon, SyncAlt as SyncAltIcon } from "@material-ui/icons"
import "./ToolbarDropdown.css"
import TrashSVG from "../../icons/TrashSvg"

const useStyles = makeStyles((theme) => ({
	header: {
		position: "sticky",
		top: 0,
		zIndex: 2,
	},
	card: {
		width: "100%",
	},
	search: {
		"& .MuiFilledInput-root": {
			border: `1px solid ${theme.palette.grey[50]}`,
			borderRadius: "3px",
			background: theme.palette.grey[50],
		},
		"& .MuiInputBase-root": {
			width: "95%",
			height: "36px",
			boxShadow: "none",
			borderRadius: "3px 3px 0px 0px",
			border: "1px solid rgba(44, 53, 51, 0.12)",
			margin: "10px 0px 0px 12px",
		},
		"& .MuiInputBase-input": {
			boxShadow: "none",
			border: "none",
		},
		"& .MuiInputBase-input:focus": {
			boxShadow: "none",
			border: "none",
		},
	},
	select: {
		"& .MuiSvgIcon-root": {
			color: theme.palette.text.primary,
		},
		"& .MuiSelect-root": {
			fontWeight: 400,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "none",
		},
		"& .MuiSelect-root:hover": {
			backgroundColor: theme.palette.primary.dark,
			color: "red",
		},

		"& .MuiSelect-select:focus": {
			background: "none",
		},
	},
	formControlSelect: {
		"&:hover .input-label-searches": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"& .input-label-searches.Mui-focused": {
			color: `${theme.palette.primary.contrastText}`,
		},

		"&:hover .MuiInputBase-root .MuiSvgIcon-root": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"& .MuiInputBase-root.Mui-focused .MuiSvgIcon-root": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"&.selected-icon-color .MuiInputBase-root .MuiSvgIcon-root": {
			color: `${theme.palette.primary.contrastText}`,
		},
		"& .MuiInputBase-root.MuiOutlinedInput-root.qa-Your.Search-select-dropdown.Mui-focused": {
			background: theme.palette.primary.dark,
		},
		"& .MuiInputBase-root.MuiOutlinedInput-root.qa-Shared.Search-select-dropdown.Mui-focused": {
			background: theme.palette.primary.dark,
		},
	},
	searchIcon: {
		marginLeft: "10px",
	},
	alphabeticalHeader: {
		...theme.typography.body2,
		fontWeight: 500,
		height: "32px",
		marginTop: "15px",
		color: theme.palette.grey["D300"],
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		paddingBottom: "15px",
		paddingRight: "22px",
		borderBottom: "solid 1px rgba(44, 53, 51, 0.12)",
	},
	deleteSavedSearchWrapper: {
		display: "inline-block",
	},
}))

const containsText = (data, searchText, nameField) => data?.[nameField]?.toLowerCase()?.indexOf(searchText.toLowerCase()) > -1

const MenuProps = {
	PaperProps: {
		style: {
			maxWidth: 486,
			minWidth: 486,
			maxHeight: 506,
			minHeight: 506,
			boxShadow: "none",
			border: "1px solid rgba(44, 53, 51, 0.12)",
		},
	},
	anchorOrigin: {
		vertical: "bottom",
		horizontal: "left",
	},
	getContentAnchorEl: null,
}

// LOAD CONCEPTS SORT
const sortAZ = (property) => {
	return function (a, b) {
		if (a?.[property]?.toLowerCase() < b?.[property]?.toLowerCase()) {
			return -1
		} else if (a?.[property]?.toLowerCase() > b?.[property]?.toLowerCase()) {
			return 1
		} else {
			return 0
		}
	}
}

function sortZA(property) {
	return function (a, b) {
		if (a[property].toLowerCase() > b[property].toLowerCase()) {
			return -1
		} else if (a[property].toLowerCase() < b[property].toLowerCase()) {
			return 1
		} else {
			return 0
		}
	}
}

const sortData = (fieldName = "description", data = [], sortType = { alphabetical: "asc", date: "" }) => {
	const filterData = [...data]
	if (filterData?.length > 0) {
		try {
			if (sortType?.alphabetical === "asc") {
				filterData.sort(sortAZ([fieldName]))
			}

			if (sortType?.alphabetical === "desc") {
				filterData.sort(sortZA([fieldName]))
			}

			if (sortType?.date === "asc") {
				const futureDate = new Date(8640000000000000)
				filterData.sort((a, b) => {
					return moment
						.utc(a?.creation_time ? a?.creation_time : futureDate)
						.diff(moment.utc(b?.creation_time ? b?.creation_time : futureDate))
				})
			}

			if (sortType?.date === "desc") {
				const pastDate = new Date(-8640000000000000)
				filterData.sort((a, b) => {
					return moment
						.utc(b?.creation_time ? b?.creation_time : pastDate)
						.diff(moment.utc(a?.creation_time ? a?.creation_time : pastDate))
				})
			}
		} catch (e) {
			//console.log(e);
		}
	}
	return filterData
}

const ToolbarDropdown = ({
	label = "Email",
	handleListChange,
	isLoading = false,
	datalist = [],
	boxStyle = { maxWidth: 160, minWidth: 160 },
	setSortConcepts = () => {},
	toolbarType = "your_concepts",
	nameField = "description",
	countField = "doc_count",
	activeTab = "your_concepts",
	setActiveTab = () => {},
	selected = "",
	setSelected = () => {},
	deleteSavedSearch,
	isDeleteOption,
	disabled = false,
}) => {
	const [sortType, setSortType] = useState({
		alphabetical: "asc",
		date: "",
	})
	const [list, setList] = useState([])
	const [t] = useTranslation("global")
	const classes = useStyles()
	const theme = useTheme()
	// const recentlyViewedList = [
	// 	{ id: "63fda271d8fed43075ff493a", description: "Concept-dew-2802", creation_time: "2023-03-02T06:12:14.782000", doc_count: 64 },
	// 	{
	// 		id: "63fda271d8fed43075ff423423a",
	// 		description: "Concept-dew-28024214142212",
	// 		creation_time: "2023-05-02T06:12:14.782000",
	// 		doc_count: 250,
	// 	},
	// 	{
	// 		id: "63fda271d8fed43075ff4942",
	// 		description: "Concept-dew-28024214142212",
	// 		creation_time: "2023-05-02T06:12:14.782000",
	// 		doc_count: 2000,
	// 	},
	// 	{
	// 		id: "63fda271d8fed43075ff4223",
	// 		description: "Concept-dew-28024214142212",
	// 		creation_time: "2023-05-02T06:12:14.782000",
	// 		doc_count: 4000,
	// 	},
	// ]
	// const alphabeticalOrderedList = [
	// 	{
	// 		id: "63fda271d8fed43075ffsada",
	// 		description: "Aegner Fog - Optimizing Subroutines",
	// 		creation_time: "2023-03-02T06:12:14.782000",
	// 		doc_count: 64,
	// 	},
	// 	{
	// 		id: "63fda271d8fed43075242143a",
	// 		description: "Concept-dew-28024214142212",
	// 		creation_time: "2022-05-02T06:12:14.782000",
	// 		doc_count: 223,
	// 	},
	// 	{
	// 		id: "63fda271d8fed43075few93a",
	// 		description: "Concept-dew-28024214142212",
	// 		creation_time: "2021-05-02T06:12:14.782000",
	// 		doc_count: 9,
	// 	},
	// 	{
	// 		id: "63fda271d8fed43075142193a",
	// 		description: "Concept-dew-28024214142212",
	// 		creation_time: "2023-26-02T06:12:14.782000",
	// 		doc_count: 50,
	// 	},
	// ]
	// const [selected, setSelected] = useState("")
	const [searchText, setSearchText] = useState("")
	//const recentlyViewedfilteredData = useMemo(() => recentlyViewedList.filter((option) => containsText(option, searchText)), [searchText])
	const alphabeticalOrderedfilteredData = useMemo(
		() => list?.filter((option) => containsText(option, searchText, nameField)),
		[list, searchText]
	)

	const handleChange = (event) => {
		setSelected(event.target.value)
		if (event.target.value) {
			setActiveTab(toolbarType)
			handleListChange(
				toolbarType,
				list.find((item) => item?.id === event.target.value)
			)
		}
	}

	// useEffect(() => {
	// 	if (selected) {
	// 		setActiveTab(toolbarType)
	// 		handleListChange(list.find((item) => item?.id === selected))
	// 	}
	// }, [selected])

	useEffect(() => {
		setList((prevList) =>
			datalist?.length > 0 && (sortType.alphabetical || sortType.date) ? [...sortData(nameField, datalist, sortType)] : []
		)
	}, [datalist, sortType])

	// useEffect(() => {
	// 	if (activeTab !== toolbarType) {
	// 		setSelected("")
	// 	}
	// }, [activeTab])

	const handleClear = () => {
		setSearchText("")
		setSortType((prevData) => ({
			alphabetical: "asc",
			date: "",
		}))
	}

	const formatDate = (date) => {
		return new Date(date).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "2-digit",
		})
	}

	const handleSortChange = (type, e) => {
		e.stopPropagation()
		setSortType((prevData) => ({
			...(type === "alphabetical" ? { date: "" } : { alphabetical: "" }),
			[type]: prevData?.[type] === "desc" ? "asc" : "desc",
		}))
	}

	//const selectedObj = [...recentlyViewedList, ...alphabeticalOrderedList].find((item) => item?.id === selected) || ""
	const selectedObj = list.find((item) => item?.id === selected) || ""

	return (
		<Box style={{ ...boxStyle }} m={1}>
			<FormControl
				fullWidth
				size="small"
				variant="outlined"
				className={`${classes.formControlSelect} ${activeTab === toolbarType ? "selected-icon-color" : ""}`}
			>
				<InputLabel
					style={{
						fontWeight: theme.typography.fontWeightMedium,
						...(activeTab === toolbarType ? { color: theme.palette.primary.contrastText } : {}),
					}}
					id={`${label}-select-label-input`}
					shrink={false}
					className={`input-label-searches ${toolbarType}#input-label-searches`}
				>
					{label}
				</InputLabel>
				{/* <Tooltip placement="top" arrow title={selectedObj?.[nameField] || ""}> */}
				<Select
					variant="outlined"
					MenuProps={MenuProps}
					labelId={`${label}-select-label-input`}
					id={`${label}-search-select`}
					// value={[...recentlyViewedList, ...alphabeticalOrderedList].find((item) => item?.id === selected)?.description}
					value={selected}
					// renderValue={(_) => (selected ? selectedObj?.[nameField] : "")}
					renderValue={(_) => null}
					label="Options"
					onChange={handleChange}
					onClose={handleClear}
					disabled={disabled}
					style={{
						...(activeTab === toolbarType
							? { backgroundColor: theme.palette.primary.dark }
							: {
									//color: theme.palette.text.primary
							  }),
					}}
					IconComponent={KeyboardArrowDown}
					className={`qa-${label}-select-dropdown ${toolbarType}#searches-dropdown ${classes.select}`}
				>
					{isLoading &&
						Array(9)
							.fill()
							.map((data, i) => (
								<MenuItem key={i} value={""}>
									<Skeleton variant="rounded" width={486} height={41} />
								</MenuItem>
							))}
					{!isLoading && list.length > 0 && (
						<div className={classes.header}>
							<Paper className={classes.card} elevation={0}>
								<TextField
									className={`qa-${label}-search-field ${classes.search}`}
									size="small"
									autoFocus
									placeholder="Search"
									fullWidth
									onChange={(e) => setSearchText(e.target.value)}
									onKeyDown={(e) => {
										if (e.key !== "Escape") {
											e.stopPropagation()
										}
									}}
									margin="none"
									variant="standard"
									InputProps={{
										disableUnderline: true,
										startAdornment: (
											<InputAdornment className={classes.searchIcon} position="start">
												<SearchIcon style={{ width: "16px", height: "16px", color: theme.palette.text.primary }} />
											</InputAdornment>
										),
									}}
								/>
							</Paper>
						</div>
					)}
					{/* <div
							style={{
								display: "flex",
								height: "32px",
								alignItems: "flex-end",
								justifyContent: "center",
								flexDirection: "column",
								marginTop: "10px",
							}}
						>
							<Typography
								style={{ fontWeight: 400, fontSize: 14, color: "#656363", marginRight: "10px" }}
								nowrap
								className="typography-ellipsis"
							>
								Total 15
							</Typography>
						</div> */}
					{/* )} */}
					{/* {!isLoading && list.length <= 0 && <ListItemText className="no-data-found-item" primary={"No Data Found"} />} */}

					{/* <ListSubheader style={{ height: "32px", marginTop: "10px" }}>
							<Typography
								style={{ ...theme.typography.body2, fontWeight: 500, color: theme.palette.grey["D300"] }}
								nowrap
								className="typography-ellipsis"
							>
								{t("filterConceptDropdown.recently_viewed")}
							</Typography>
						</ListSubheader> */}
					{/* {recentlyViewedList.length > 0 &&
							recentlyViewedfilteredData.map((option, i) => (
								<MenuItem
									key={option?.id}
									value={option?.id}
									style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
									className={`qa-${label}-recently-viewed-menu-item-${i}`}
								> */}
					{/* <Checkbox checked={selectedOptions.indexOf(option) > -1} /> */}
					{/* <Tooltip placement="top" arrow title={option}> */}
					{/* <Grid item>
										<Typography
											style={{ ...theme.typography.body2, fontWeight: 400 }}
											nowrap
											className="typography-ellipsis"
										>
											{option?.[nameField]}
										</Typography>
										<Typography
											style={{ ...theme.typography.body2, fontSize: 11, color: theme.palette.grey["D300"] }}
											nowrap
											className="typography-ellipsis"
										>
											{option?.creation_time ? formatDate(option?.creation_time) : "—"}
										</Typography>
									</Grid>
									<Grid item style={{ alignSelf: "start" }}>
										<Typography
											style={{ ...theme.typography.body2, color: theme.palette.grey["D300"], fontWeight: 400 }}
											nowrap
											className="typography-ellipsis"
										>
										{option.[countField] >= 0 ? option.[countField] : "—"}
										</Typography>
									</Grid> */}
					{/* </Tooltip> */}
					{/* </MenuItem> */}
					{/* ))} */}
					{!isLoading && list.length <= 0 && (
						<ListItemText className="no-data-found-item" primary={t("filterConceptDropdown.noDataFound")} />
					)}

					{!isLoading && list.length > 0 && (
						<ListSubheader className={classes.alphabeticalHeader}>
							<Grid item style={{ display: "flex", minWidth: "250px" }}>
								<div onClick={(e) => handleSortChange("alphabetical", e)}>
									<Grid
										container
										direction="row"
										alignItems="center"
										justifyContent="center"
										style={{ cursor: "pointer" }}
									>
										<Grid item>{t("filterConceptDropdown.alphabetical")}</Grid>
										<SyncAltIcon style={{ transform: "rotate(90deg)", width: "15px", height: "15px" }} />
									</Grid>
								</div>
							</Grid>
							<Grid item>
								<div onClick={(e) => handleSortChange("date", e)}>
									<Grid
										container
										direction="row"
										alignItems="center"
										justifyContent="center"
										style={{ cursor: "pointer" }}
									>
										<Grid item style={{ fontWeight: 400, fontSize: 12, color: theme.palette.grey["D300"] }}>
											{t("filterConceptDropdown.date")}
										</Grid>
										<SyncAltIcon style={{ transform: "rotate(90deg)", width: "15px", height: "15px" }} />
									</Grid>
								</div>
							</Grid>
							{isDeleteOption && <Grid></Grid>}
						</ListSubheader>
					)}
					{!isLoading &&
						list.length > 0 &&
						alphabeticalOrderedfilteredData.map((option, i) => (
							<MenuItem
								key={option?.id}
								value={option?.id}
								style={{
									display: "flex",
									alignItems: "flex-start",
									justifyContent: "space-between",
									borderBottom: "solid 1px rgba(44, 53, 51, 0.12)",
									paddingRight: "22px",
								}}
								className={`qa-${label}-alphabetical-menu-item-${i}`}
							>
								<Grid item style={{ minWidth: "250px", maxWidth: "250px" }}>
									<Tooltip placement="top-start" arrow title={option?.[nameField]}>
										<Typography
											style={{ ...theme.typography.body2, fontWeight: 400 }}
											nowrap
											className="typography-ellipsis"
										>
											{option?.[nameField]}
										</Typography>
									</Tooltip>

									<Typography
										style={{ ...theme.typography.body2, fontWeight: 400, color: theme.palette.grey["D300"] }}
										nowrap
										className="typography-ellipsis"
									>
										{option[countField] >= 0 ? option[countField] : "—"}
									</Typography>
								</Grid>
								<Grid item style={{ alignSelf: "start", maxWidth: "100px" }}>
									<Typography style={{ fontSize: 11 }} nowrap className="typography-ellipsis">
										{option?.creation_time ? formatDate(option?.creation_time) : "—"}
									</Typography>
								</Grid>
								{isDeleteOption && (
									<Grid>
										<span
											className={classes.deleteSavedSearchWrapper}
											onClick={(event) => deleteSavedSearch(event, option?.id)}
										>
											<TrashSVG />
										</span>
									</Grid>
								)}
							</MenuItem>
						))}
				</Select>
				{/* </Tooltip> */}
			</FormControl>
		</Box>
	)
}

export default ToolbarDropdown
